import { Box, Checkbox, FormControl, FormHelperText, Grid, OutlinedInput, TableCell, TableRow } from "@mui/material";
import { CustomFormControl, DecimalOrEmptyFormControl, IntegerOrEmptyFormControl } from "../../../../../components/FormControl";
import DateInput from "../../../../../components/DateInput";
import { validateDecimal, validateDecimalOrEmpty, validateInteger, validateIntegerOrEmpty } from "../../../../../utils/validation";
import { inputValueToDecimal, inputValueToInt } from "../../../../../utils/utils";
import { green, red } from "@mui/material/colors";
import SelectInput from "./SelectInput";
import React, { useCallback, useMemo } from "react";

const priceTableColumnSx = {
  fontSize: 18,
};

const getBackgroundColor = (errors) => {
  return Object.keys(errors).length > 0 ? red[50] : null;
}

function CsvReadingResultRow ({
  result, selectOptions, onChangeRowValue, onChangeEnabledPreservation
}) {

  const {
    index, enabledPreservation, values, errors
  } = useMemo(()=> result, [result.index, result.enabledPreservation, result.values, result.errors]);

  const ShopSelect = useMemo(() => ({shopIdValue, onSelect}) => (
    <SelectInput
      value={shopIdValue}
      items={selectOptions.shops}
      getId={(shop) => shop.shopId}
      getLabel={(shop) => shop.customerName + " " + shop.shopName}
      match={(shop, value) => (shop.customerName + " " + shop.shopName).includes(value)}
      onSelect={onSelect}
      width='200px'
    />
  ), [selectOptions.shops]);

  const ProductSelect = useMemo(() => ({productIdValue, onSelect}) => (
    <SelectInput
      value={productIdValue}
      items={selectOptions.products}
      getId={(prod) => prod.productId}
      getLabel={(prod) => prod.productId + " - " + prod.productName}
      onSelect={onSelect}
      width='150px'
    />
  ), [selectOptions.products]);

  const OriginSelect = useMemo(() => ({originIdValue, onSelect}) => (
    <SelectInput
      value={originIdValue}
      items={selectOptions.origins}
      getId={(origin) => origin.originId}
      getLabel={(origin) => origin.originId + " - " + origin.originName}
      onSelect={onSelect}
      width='150px'
    />
  ), [selectOptions.origins]);

  const RankSelect = useMemo(() => ({rankIdValue, onSelect}) => (
    <SelectInput
      value={rankIdValue}
      items={selectOptions.ranks}
      getId={(rank) => rank.rankId}
      getLabel={(rank) => rank.rankId + " - " + rank.rankName}
      onSelect={onSelect}
      width='80px'
    />
  ),[selectOptions.ranks]);

  const ProductClassSelect = useMemo(() => ({productClassIdValue, onSelect}) => (
    <SelectInput
      value={productClassIdValue}
      items={selectOptions.productClasses}
      getId={(productClass) => productClass.productClassId}
      getLabel={(productClass) => productClass.productClassId + " - " + productClass.productClassName}
      onSelect={onSelect}
      width='80px'
    />
  ) ,[selectOptions.productClasses]);

  const PackingSelect = useMemo(() => ({packingIdValue, onSelect}) => (
    <SelectInput
      value={packingIdValue}
      items={selectOptions.packings}
      getId={(packing) => packing.packingId}
      getLabel={(packing) => packing.packingId + " - " + packing.packingName}
      onSelect={onSelect}
      width='80px'
    />
  ), [selectOptions.packings]);

  const WeightUnitSelect = useMemo(() => ({weightUnitIdValue, onSelect}) => (
    <SelectInput
      value={weightUnitIdValue}
      items={selectOptions.weightUnits}
      getId={(weightUnit) => weightUnit.weightUnitId}
      getLabel={(weightUnit) => weightUnit.weightUnitName}
      onSelect={onSelect}
      match={(weightUnit, value) => weightUnit.weightUnitName.includes(value)}
      width='50px'
    />
  ), [selectOptions.weightUnits]);

  const OrderingUnitSelect = useMemo(() => ({orderingUnitIdValue, onSelect}) => (
    <SelectInput
      value={orderingUnitIdValue}
      items={selectOptions.orderingUnits}
      getId={(orderingUnit) => orderingUnit.orderingUnitId}
      getLabel={(orderingUnit) => orderingUnit.orderingUnitName}
      onSelect={onSelect}
      match={(orderingUnit, value) => orderingUnit.orderingUnitName.includes(value)}
      width='50px'
    />
  ), [selectOptions.orderingUnits]);

  const handleChangeEnabledPreservation = useCallback((enabledPreservation) => {

    onChangeEnabledPreservation(index)(enabledPreservation);
  }, [onChangeEnabledPreservation, index]);


  const handleChangeValue = useCallback((prop) => (v) => {

    onChangeRowValue(index)({[prop]: v});
  }, [onChangeRowValue, index]);

  return (
    <TableRow
      sx={{
        alignItems: "baseline",
        bgcolor: getBackgroundColor(errors),
      }}
    >
      <TableCell>
        <Checkbox
          checked={enabledPreservation}
          onChange={(e) => handleChangeEnabledPreservation(e.target.checked)}
        />
      </TableCell>
      <TableCell
        sx={{
          ...priceTableColumnSx,
          height: 28
        }}
      >
        <FormControl error={Boolean(errors.shopId)}>
          <ShopSelect
            shopIdValue={values.shopId}
            onSelect={handleChangeValue('shopId')}
          />
          <FormHelperText
            sx={{mx: 0}}
          >
            {errors.shopId}
          </FormHelperText>
        </FormControl>
      </TableCell>
      <TableCell
        component="th"
        scope="row"
        sx={{
          ...priceTableColumnSx,
        }}
      >
        <FormControl error={Boolean(errors.productId)}>
          <ProductSelect
            productIdValue={values.productId}
            onSelect={handleChangeValue('productId')}
          />
          <FormHelperText sx={{mx: 0}}>
            {errors.productId}
          </FormHelperText>
        </FormControl>
      </TableCell>
      <TableCell
        sx={{
          ...priceTableColumnSx,
        }}
      >
        <FormControl error={Boolean(errors.originId)}>
          <OriginSelect
            originIdValue={values.originId}
            onSelect={handleChangeValue('originId')}
          />
          <FormHelperText sx={{mx: 0}}>
            {errors.originId}
          </FormHelperText>
        </FormControl>
      </TableCell>
      <TableCell
        sx={{ ...priceTableColumnSx }}
      >
        <FormControl error={Boolean(errors.rankId)}>
          <RankSelect
            rankIdValue={values.rankId}
            onSelect={handleChangeValue('rankId')}
          />
          <FormHelperText sx={{mx: 0}}>
            {errors.rankId}
          </FormHelperText>
        </FormControl>
      </TableCell>
      <TableCell
        sx={{ ...priceTableColumnSx }}
      >
        <FormControl error={Boolean(errors.productClassId)}>
          <ProductClassSelect
            productClassIdValue={values.productClassId}
            onSelect={handleChangeValue('productClassId')}
          />
          <FormHelperText sx={{mx: 0}}>
            {errors.productClassId}
          </FormHelperText>
        </FormControl>
      </TableCell>
      <TableCell
        sx={{ ...priceTableColumnSx }}
      >
        <FormControl
          error={Boolean(errors.packingId)}
        >
          <PackingSelect
            packingIdValue={values.packingId}
            onSelect={handleChangeValue('packingId')}
          />
          <FormHelperText sx={{mx: 0}}>
            {errors.packingId}
          </FormHelperText>
        </FormControl>
      </TableCell>
      <TableCell
        sx={{ ...priceTableColumnSx}}
      >
        <FormControl error={Boolean(errors.weight)}>
          <Grid container alignItems="center" columnSpacing={1} width='150px'>
            <Grid item>
              <DecimalOrEmptyFormControl
                value={values.weight?.toLocaleString() || ''}
                setValue={(v) => handleChangeValue("weight")(validateDecimal(v) ? inputValueToDecimal(v).toString() : v)}
                inputProps={{
                  sx: { ...priceTableColumnSx, textAlign: "right" },
                }}
                onBlurFormat={(v) => validateDecimal(v) ? inputValueToDecimal(v).toString() : v}
                sx={{ width: 80 }}
                helperText=""
              /> 
            </Grid>
            <Grid item>              
              <WeightUnitSelect
                weightUnitIdValue={values.weightUnitId}
                onSelect={handleChangeValue('weightUnitId')}
              />
            </Grid>
          </Grid>
          <FormHelperText sx={{mx: 0}}>
            {errors.weight}
          </FormHelperText>
        </FormControl>
      </TableCell>

      <TableCell
        sx={{ ...priceTableColumnSx }}
      >
        <CustomFormControl
          value={values.unitQuantity || ''}
          setValue={(v) => handleChangeValue("unitQuantity")(v || "")}
          sx={{width:"80px"}}
        />
      </TableCell>
      <TableCell
        sx={{
          ...priceTableColumnSx
        }}
      >
        <IntegerOrEmptyFormControl
          error={Boolean(errors.price)}
          value={values.price?.toLocaleString() || ""}
          setValue={(v) => handleChangeValue("price")(validateInteger(v) ? inputValueToInt(v) : v)}
          inputProps={{
            sx: { ...priceTableColumnSx, textAlign: "right" },
          }}
          onBlurFormat={(v) => validateInteger(v) ? inputValueToInt(v).toLocaleString() : v}
          sx={{ width: 130 }}
          helperTextSx={{mx: 0, width: 130}}
          helperText={errors.price}
        />
      </TableCell>
      
      <TableCell
        sx={{...priceTableColumnSx}}
      >
        <FormControl error={Boolean(errors.orderQuantity)}>
          <Grid container alignItems="center" columnSpacing={1} width='150px'>
            <Grid item>
              <DecimalOrEmptyFormControl
                value={values.orderQuantity?.toLocaleString() || ""}
                setValue={(v) => handleChangeValue("orderQuantity")(validateDecimal(v) ? inputValueToDecimal(v).toString() : v)}
                inputProps={{
                  sx: { ...priceTableColumnSx, textAlign: "right" },
                }}
                onBlurFormat={(v) => validateDecimal(v) ? inputValueToDecimal(v).toString() : v}
                sx={{ width: 80 }}
                helperText=""
              /> 
            </Grid>
            <Grid item>
              <OrderingUnitSelect
                orderingUnitIdValue={values.orderingUnitId}
                onSelect={handleChangeValue('orderingUnitId')}
              />
            </Grid>
          </Grid>
          <FormHelperText sx={{mx: 0}}>
            {errors.orderQuantity || ''}
          </FormHelperText>
        </FormControl>
      </TableCell>
      <TableCell
        sx={{...priceTableColumnSx}}
      >
        <DateInput
          inputProps={{
            sx: { ...priceTableColumnSx },
          }}
          value={values.shippingDate}
          setValue={(v) => handleChangeValue("shippingDate", v)}
        />
      </TableCell>
    </TableRow>
  );
}

export default React.memo(CsvReadingResultRow)
